// 特集記事作成（ArticleCreator）関連のドメインモデル

// -----------------------------------------------
// 共通
// -----------------------------------------------

export type PriorityLevel = 'high' | 'medium' | 'low'

// 分析履歴（article/history はパース済みJSONを返す）
export interface ArticleAnalysisHistory {
  id: number
  site_id: number
  analysis_type: string
  input_data: Record<string, unknown>
  execution_source: string | null
  summary: string | null
  created_at: string | null
}

// -----------------------------------------------
// キーワード選定
// -----------------------------------------------

export interface KeywordItem {
  keyword: string
  monthly_search_volume: number
  cve: number
  difficulty_stars: number
  search_rank: number
  ranking_url?: string | null
  contains_input: boolean
  relation_type?: string | null
  article_format?: string | null
  required_child_count?: number
  genre_analysis?: Record<string, number> | null
}

export interface KeywordAnalysisSummary {
  total_keywords: number
  input_containing: number
  best_opportunity: string
  recommendation: string
}

export interface KeywordAnalysis {
  keywords?: KeywordItem[]
  summary?: KeywordAnalysisSummary
  error?: string
}

export interface KeywordAnalysisResult {
  analysis?: KeywordAnalysis
  input_keyword?: string
  suggest_count?: number
  site_url?: string | null
}

// -----------------------------------------------
// AI記事生成
// -----------------------------------------------

export interface InternalLinkSuggestion {
  anchor_text: string
  target_hint: string
}

export interface CtaSuggestion {
  text: string
  placement?: string
  type?: string
}

export interface GeneratedArticle {
  title?: string
  meta_description?: string
  // 本文はコンテンツエリア上部・下部それぞれのHTMLとして生成される
  content_area_top_html?: string
  content_area_bottom_html?: string
  total_word_count?: number
  seo_keywords?: string[]
  internal_link_suggestions?: InternalLinkSuggestion[]
  cta_suggestions?: CtaSuggestion[]
  error?: string
}

// -----------------------------------------------
// AI記事構成案生成（本文抜き）
// -----------------------------------------------

export interface InternalLinkCandidate {
  article_proposal_id: number
  title: string
  slug: string
  status: string
}

export interface GeneratedOutline {
  title_candidates: string[]
  meta_description: string
  heading_outline: string[]
  tone: string
  target_length: number
  internal_link_candidates: InternalLinkCandidate[]
}

// -----------------------------------------------
// コンテンツギャップ
// -----------------------------------------------

export interface ContentGapItem {
  area: string
  description?: string
  priority?: PriorityLevel
  estimated_search_volume?: PriorityLevel
  suggested_article_title?: string
  potential_keywords?: string[]
  related_categories?: string[]
  related_brands?: string[]
}

export interface ContentGapQuickWin {
  action: string
  expected_impact?: string
}

export interface ContentGapAnalysis {
  coverage_analysis?: {
    well_covered?: string[]
    partially_covered?: string[]
    not_covered?: string[]
  }
  gaps?: ContentGapItem[]
  quick_wins?: ContentGapQuickWin[]
  error?: string
}

// 拡張: カニバリゼーション
export interface CannibalizationPair {
  page_a_title: string
  page_b_title: string
  overlap_score?: number
  severity?: PriorityLevel
  recommended_action?: string
  action_detail?: string
  overlapping_keywords?: string[]
}

export interface CannibalizationAnalysis {
  summary?: {
    total_pages?: number
    issue_count?: number
    high_severity_count?: number
    recommendation?: string
  }
  cannibalization?: CannibalizationPair[]
  error?: string
}

// 拡張: 内部リンク構造
export interface OrphanPage {
  page_id?: string | number
  title: string
  reason?: string
  suggested_link_from?: string[]
}

export interface SuggestedLink {
  from_id?: string | number
  from_title: string
  to_id?: string | number
  to_title: string
  anchor_text?: string
  reason?: string
  priority: PriorityLevel
}

export interface TopicCluster {
  cluster_name: string
  pillar_page?: string
  satellite_pages?: string[]
  missing_satellites?: string[]
}

export interface LinkStructureAnalysis {
  summary?: {
    total_pages?: number
    health_score?: number
    orphan_count?: number
    weak_count?: number
    suggested_link_count?: number
    cluster_count?: number
    recommendation?: string
  }
  link_structure?: {
    orphan_pages?: OrphanPage[]
    suggested_links?: SuggestedLink[]
    topic_clusters?: TopicCluster[]
    weak_pages?: { title: string; reason?: string }[]
  }
  error?: string
}

// 拡張分析の種別
export const ExtGapType = {
  CANNIBALIZATION: 'cannibalization',
  LINK_STRUCTURE: 'link_structure'
} as const
export type ExtGapType = (typeof ExtGapType)[keyof typeof ExtGapType]

// 拡張: 前回比較（タイプごとにフィールドが異なる）
export interface ExtGapComparison {
  available: boolean
  message?: string
  current_date?: string | null
  previous_date?: string | null
  // cannibalization
  new_pairs?: { page_a: string; page_b: string; score?: number }[]
  resolved_pairs?: { page_a: string; page_b: string }[]
  score_changes?: {
    page_a_title: string
    page_b_title: string
    previous_score: number
    current_score: number
    change: number
  }[]
  previous_count?: number
  current_count?: number
  // link_structure
  previous_health?: number
  current_health?: number
  health_score_change?: number
  orphan_change?: number
  suggested_link_change?: number
  new_orphans?: string[]
  resolved_orphans?: string[]
}

// 拡張: 時系列分析
export interface GapTimelineRow {
  date: string
  [metric: string]: string | number
}

export interface GapTimelineAiEvaluation {
  period_evaluation?: {
    overall_trend?: string
    score?: number
    summary?: string
    highlights?: string[]
  }
  priority_actions?: {
    rank?: number
    action: string
    reason?: string
    expected_impact?: PriorityLevel
    timeframe?: string
  }[]
  risk_alerts?: { level?: 'critical' | 'warning' | 'info'; message: string }[]
  error?: string
}

export interface GapTimelineResult {
  available: boolean
  message?: string
  period_start?: string
  period_end?: string
  analysis_count?: number
  timeline_data?: GapTimelineRow[]
  ai_evaluation?: GapTimelineAiEvaluation
}
